import styles from './table.module.scss';

export default function Table<const T extends string>({
	columns,
	rows,
	className = null,
	mergeUnlabeledRows = true,
}: TableProps<T>) {
	const hasColHeaderRow = columns.some((col) => col.label);
	const hasColWidths = columns.some((col) => col.width);

	return (
		<table className={[styles.table, className].filter(Boolean).join(' ')}>
			{hasColWidths && (
				<colgroup>
					{columns.map(({ key, width }) => (
						<col key={key} style={width ? { width } : undefined} />
					))}
				</colgroup>
			)}
			{hasColHeaderRow && (
				<thead>
					<tr>
						{columns.map(
							({ key, label }) =>
								label && (
									<th scope="col" key={key}>
										{label}
									</th>
								),
						)}
					</tr>
				</thead>
			)}
			<tbody>
				{rows.map((row, i) => {
					const headerColumnKey = columns[0].key;
					const isMergedRow = mergeUnlabeledRows && !row.cells[headerColumnKey];

					return (
						<tr
							className={isMergedRow ? styles.mergedRow : ''}
							key={row.key ?? i}
						>
							{columns.map((col, j) => {
								const isHeaderColumn = j === 0;
								const Cell = isHeaderColumn ? 'th' : 'td';
								const value = row.cells[col.key];

								return (
									<Cell
										{...(isHeaderColumn ? { scope: 'row' } : null)}
										key={`${col.key}-${value}`}
									>
										{value}
									</Cell>
								);
							})}
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

type Column<T extends string> = {
	key: T;
	label?: string;
	width?: string | number;
};

type Row<T extends string> = {
	key?: string;
	cells: Record<T, string>;
};

type TableProps<T extends string> = {
	columns: readonly Column<T>[];
	rows: readonly Row<T>[];
	mergeUnlabeledRows?: boolean;
	className?: string;
};
